/**
 * Subdomain detection · v2.6.7 batch D (Phase 3 subdomain split).
 *
 * Reads `window.location.hostname` once and classifies the request into one
 * of 5 buckets so each subdomain can default to its intended landing page:
 *
 *   ride.bullertaxi.com    → "ride"    (customer dashboard / sign-in)
 *   driver.bullertaxi.com  → "driver"  (driver console / sign-in)
 *   admin.bullertaxi.com   → "admin"   (dispatch / admin console)
 *   portal.bullertaxi.com  → "portal"  (accounting + onboarding portal)
 *   bullertaxi.com         → "default" (legacy unified login page)
 *   *.preview.emergentagent.com → "default"  (dev preview always shows full app)
 *
 * If the user navigates inside the app (e.g., admin opens /portal manually)
 * those routes are NOT blocked — we just redirect the root path `/` to the
 * subdomain's home so a deep link still works. We never hide functionality
 * behind a wrong subdomain because the user might intentionally bookmark
 * a specific route while on a different subdomain.
 */

export function getSubdomain(host) {
  // v2.6.7 batch D dev override · ?_sub=ride|driver|admin|portal in the URL
  // (or localStorage key `rtg_sub_override` = same values) forces the helper
  // to report that subdomain even on the apex/preview origin. Lets you
  // verify the subdomain split BEFORE adding real DNS, and lets QA/agents
  // E2E-test each subdomain from a single deployment URL.
  //
  // ONLY consulted when `host` arg is not provided (i.e., we're reading
  // window.location). Pure-function callers (`getSubdomain('ride.foo')`)
  // skip this branch so unit tests stay deterministic.
  if (host === undefined && typeof window !== "undefined") {
    try {
      const q = new URLSearchParams(window.location.search).get("_sub");
      const allowed = ["ride", "driver", "admin", "portal", "default"];
      if (q && allowed.includes(q)) { window.localStorage.setItem("rtg_sub_override", q); return q; }
      const saved = window.localStorage.getItem("rtg_sub_override");
      if (saved && allowed.includes(saved)) return saved;
    } catch (e) {
      // localStorage may be disabled in private browsing / locked-down mobile
      // webviews — that's fine, we fall back to hostname-based detection.
      console.warn("subdomain override storage blocked", e?.message);
    }
  }
  const h = (host || (typeof window !== "undefined" ? window.location.hostname : "")).toLowerCase();
  // Strip port, if any
  const bare = h.split(":")[0];
  // Preview / dev / localhost — never trigger subdomain routing
  if (!bare || bare === "localhost" || bare.endsWith(".preview.emergentagent.com") || bare.startsWith("127.") || bare.startsWith("0.0.0.0")) {
    return "default";
  }
  // Take the first dot-segment
  const first = bare.split(".")[0];
  if (first === "ride")    return "ride";
  if (first === "driver")  return "driver";
  if (first === "admin")   return "admin";
  if (first === "portal")  return "portal";
  return "default";
}

/**
 * The path the user should land on when they hit the root URL `/` of a
 * given subdomain. Used by <SubdomainRootRedirect/> to bounce them.
 */
export function getSubdomainHome(sub) {
  switch (sub) {
    case "ride":   return "/ride";          // → CustomerProtected → /ride/login if not signed in
    case "driver": return "/";              // legacy "/" is the driver Dashboard (Protected)
    case "admin":  return "/admin";         // AdminProtected → /admin/login if not signed in
    case "portal": return "/portal";        // Same protected chain → /admin/login → AdminGate → portal
    default:       return null;             // null = no redirect, render the apex login
  }
}

/**
 * The login tab to default to on the unified /login page when accessed via
 * a specific subdomain. This is what makes ride.bullertaxi.com/login open
 * the Ride tab pre-selected, etc.
 */
export function getDefaultLoginTab(sub) {
  if (sub === "ride")    return "ride";
  if (sub === "driver")  return "driver";
  if (sub === "admin")   return "dispatch";
  if (sub === "portal")  return "portal";    // v2.6.7 batch E: portal subdomain → Portal tab
  // v2.8 · apex default is DRIVER so the combined /login stays usable for staff;
  // riders are funneled to the dedicated /ride/login portal (homepage CTA +
  // the ride-tab redirect in LoginPage), so ride is no longer the apex default.
  return "driver";                            // apex default
}
